// CTV-785 review sessions: the Waymark SDK re-broadcasts every iframe event
// on `document` (a CustomEvent named `waymark:<event>`, see the SDK's
// `sendEvent`), so when two SDK instances share a page each one hears the
// other's events. Two instances coexist by design since CTV-785: the
// surface's WaymarkProvider stays on the ACTIVE AI commercial (the newest
// uncommitted one, with its live generation subscriptions), and a short-lived
// editor session (useWaymarkEditorSession) reviews a committed one. This
// module is the arbiter both consult before acting on an event:
//
//   - Editor ownership. Only one Waymark editor is visible at a time, and
//     `videoCompleted` / `editorExited` belong to whoever opened it. Every
//     open claims; a handler drops the event while someone else holds the
//     claim. No claim at all never counts as "someone else", so a provider
//     that never shared the page behaves exactly as before.
//   - Session videos. A review session registers the video it is editing so
//     the provider never adopts that video's `videoRendered` as its own. The
//     provider may hold no videoId of its own yet (mid-analysis, or idle), so
//     a plain video-id comparison on its side cannot protect it.
//
// Module state, not React state: the SDK events are global, so the arbiter
// has to be too. Reset between tests with __resetEditorSessionsForTests.

let editorOwner: string | null = null;
const sessionVideos = new Set<string>();

/** Record `owner` as the session that just opened the editor. */
export function claimEditor(owner: string): void {
  editorOwner = owner;
}

/** Drop the claim, but only if `owner` still holds it. */
export function releaseEditor(owner: string): void {
  if (editorOwner === owner) editorOwner = null;
}

/** True when a different session holds the editor claim. */
export function editorOwnedByOther(owner: string): boolean {
  return editorOwner !== null && editorOwner !== owner;
}

/** A review session is editing this video; the provider must not adopt its renders. */
export function registerSessionVideo(videoId: string): void {
  sessionVideos.add(videoId);
}

export function unregisterSessionVideo(videoId: string): void {
  sessionVideos.delete(videoId);
}

export function isSessionVideo(videoId: string | null | undefined): boolean {
  return !!videoId && sessionVideos.has(videoId);
}

export function __resetEditorSessionsForTests(): void {
  editorOwner = null;
  sessionVideos.clear();
}
